import { PollFilledIcon, Tooltip, palette } from "@minervaproject/fds";
import { truncate } from "lodash";

import { useUserIdsWithCapability } from "hooks/capabilities";
import { useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useUsersProperties } from "hooks/use_user_properties";
import { CapabilityType, PollSessionStatusType } from "shared/types";
import { classroomSelectors } from "store";

function PollResponseCounter({
  pollSessionId,
  totalCount,
}: {
  pollSessionId?: string;
  totalCount: number;
}) {
  const pollSession = useAppSelector(classroomSelectors.selectPollSessionById(pollSessionId));
  const poll = useAppSelector(classroomSelectors.selectPollById(pollSession?.poll_id));
  const pollResponses = useArraySelector(
    classroomSelectors.selectPollResponsesByPollSessionId(pollSessionId || ""),
  ).filter((pollResponse) => !pollResponse.is_draft);

  if (!pollSessionId || pollSession?.status !== PollSessionStatusType.InProgress) return null;

  const contents = (
    <div className="flex items-center mb1" data-testid="poll-response-counter">
      <PollFilledIcon width={16} height={16} />
      <div className="mx2 rollup-bar flex-auto flex content-start">
        <div
          className="rollup-bar-content"
          style={{ width: `${(100 * pollResponses.length) / totalCount}%` }}
          data-testid="rollup-bar-content"
        />
      </div>
      <span
        className="body-s text-black-tint-70 count"
        style={{ marginBottom: "2px" }}
        data-testid="poll-response-count"
      >
        {pollResponses.length}
      </span>
    </div>
  );

  return (
    <Tooltip
      backgroundColor={palette.blackTint20}
      iconComponent={contents}
      place="left"
      text={truncate(poll?.question, { length: 120, separator: /,? +/ })}
      textColor="white"
    />
  );
}

function PollRollup() {
  const pollSessionIds = useArraySelector(classroomSelectors.selectPollSessionIdsOnStage());
  const canParticipateInClassUserIds = useUserIdsWithCapability(
    CapabilityType.CAN_PARTICIPATE_IN_CLASS,
  );
  const users = useUsersProperties(["is_present"]);
  const presentStudents = Object.values(users).filter(
    ({ id, is_present }) => canParticipateInClassUserIds.includes(id) && is_present,
  );
  const totalCount = presentStudents.length;

  return (
    <div className="px2 py1">
      {pollSessionIds.map((pollSessionId) => (
        <PollResponseCounter
          key={pollSessionId}
          pollSessionId={pollSessionId}
          totalCount={totalCount}
        />
      ))}
    </div>
  );
}

export { PollResponseCounter, PollRollup };
